import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { BsCopy, BsBoxArrowUpRight } from 'react-icons/bs';
import ModalHelp from '../../../components/ModalHelp';
import AnimatedModal from './AnimatedModal';

interface SalaEsperaModalProps {
  show: boolean;
  onClose: () => void;
  processo: any;
  onSave: (processoId: string, salaUrl: string) => Promise<void>;
  onProcessoUpdated?: () => void;
}

const SalaEsperaModal: React.FC<SalaEsperaModalProps> = ({ show, onClose, processo, onSave, onProcessoUpdated }) => {
  const [salaUrl, setSalaUrl] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (processo?.salaEspera?.salaUrl) {
      setSalaUrl(processo.salaEspera.salaUrl);
    } else {
      setSalaUrl('');
    }
  }, [processo]);

  if (!show) return null;

  const handleSave = async () => {
    if (!salaUrl.trim()) {
      toast.error('Por favor, insira uma URL válida para a sala de espera.');
      return;
    }

    try {
      setLoading(true);
      await onSave(processo.id, salaUrl.trim());
      toast.success('Sala de espera salva com sucesso!');
      onProcessoUpdated?.();
      onClose();
    } catch (error) {
      toast.error('Erro ao salvar sala de espera.');
    } finally {
      setLoading(false);
    }
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(salaUrl);
      toast.success('URL copiada para a área de transferência!');
    } catch (error) {
      toast.error('Erro ao copiar URL');
    }
  };

  const handleOpenUrl = () => {
    if (isValidUrl(salaUrl)) {
      window.open(salaUrl, '_blank');
    }
  };

  return (
    <AnimatedModal isOpen={show} onClose={onClose}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        minWidth: '500px',
        maxWidth: '600px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}>
        <div className="modal-header" style={{
          borderBottom: '1px solid #e9ecef',
          paddingBottom: '16px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            Sala de Espera - {processo?.nome}
          </h3>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-body" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              URL da Sala de Espera:
            </label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="url"
                value={salaUrl}
                onChange={(e) => setSalaUrl(e.target.value)}
                placeholder="https://exemplo.com/sala-espera"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  paddingRight: salaUrl && isValidUrl(salaUrl) ? '80px' : '12px',
                  border: `1px solid ${salaUrl && !isValidUrl(salaUrl) ? '#dc3545' : '#ced4da'}`,
                  borderRadius: '4px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
              {salaUrl && isValidUrl(salaUrl) && (
                <div style={{
                  position: 'absolute',
                  right: '8px',
                  display: 'flex',
                  gap: '4px'
                }}>
                  <button
                    type="button"
                    onClick={handleCopyUrl}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6c757d',
                      padding: '4px',
                      borderRadius: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px'
                    }}
                    title="Copiar URL"
                  >
                    <BsCopy />
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenUrl}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6c757d',
                      padding: '4px',
                      borderRadius: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px'
                    }}
                    title="Abrir em nova janela"
                  >
                    <BsBoxArrowUpRight />
                  </button>
                </div>
              )}
            </div>
            {salaUrl && !isValidUrl(salaUrl) && (
              <small style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Por favor, insira uma URL válida (ex: https://exemplo.com)
              </small>
            )}
          </div>

          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#6c757d'
          }}>
            <strong>Dica:</strong> A URL deve incluir o protocolo (http:// ou https://) e ser um link válido para a sala de espera virtual.
          </div>
        </div>

        <div className="modal-footer" style={{
          borderTop: '1px solid #e9ecef',
          paddingTop: '16px',
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '8px 16px',
              border: '1px solid #6c757d',
              borderRadius: '4px',
              backgroundColor: 'white',
              color: '#6c757d',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !salaUrl.trim() || !isValidUrl(salaUrl)}
            style={{
              padding: '8px 16px',
              border: '1px solid #28a745',
              borderRadius: '4px',
              backgroundColor: loading || !salaUrl.trim() || !isValidUrl(salaUrl) ? '#6c757d' : '#28a745',
              color: 'white',
              cursor: loading || !salaUrl.trim() || !isValidUrl(salaUrl) ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default SalaEsperaModal;